import { useEffect, useState } from 'react';
import { isEffectivelyOffline, onConnectionStallChange } from '../lib/connectionHealth';

/**
 * React binding for `isEffectivelyOffline()` (see lib/connectionHealth.ts).
 *
 * #1871 lane-tech-offline-data round 2. `TechJobDetail.tsx` and
 * `useServiceReportLauncher.ts` previously called `isEffectivelyOffline()`
 * once, inline, at render — so the offline-vs-genuinely-missing message it
 * drives never updated after the first paint. A technician who regained
 * signal, or whose link cleared a stall (connectionHealth's hysteresis:
 * `onConnectionStallChange`), kept seeing the stale offline copy until
 * something UNRELATED forced a re-render.
 *
 * `onConnectionStallChange` covers the STALL half of `isEffectivelyOffline()`
 * (real operations failing despite `navigator.onLine`); the browser's
 * `online`/`offline` events cover the other half (`!navigator.onLine`), which
 * `onConnectionStallChange` emits nothing for on its own.
 */
export function useIsOffline(): boolean {
  const [offline, setOffline] = useState(() => isEffectivelyOffline());

  useEffect(() => {
    const recompute = () => setOffline(isEffectivelyOffline());
    const unsubscribeStall = onConnectionStallChange(recompute);
    window.addEventListener('online', recompute);
    window.addEventListener('offline', recompute);
    return () => {
      unsubscribeStall();
      window.removeEventListener('online', recompute);
      window.removeEventListener('offline', recompute);
    };
  }, []);

  return offline;
}
